import React, { useCallback } from 'react';
import MyFilesListView from '../../features/file-manager/components/my-files/my-files-list-view';
import { ShareWithMeModal } from 'features/file-manager/components/modals/shared-user-modal';
import { RenameModal } from 'features/file-manager/components/modals/rename-modal';
import MyFileGridView from 'features/file-manager/components/my-files/my-files-grid-view';
import { FileManagerLayout } from 'features/file-manager/file-manager-layout';
import { FileViewRenderer } from 'features/file-manager/components/file-view-renderer';
import { useFileManager } from 'features/file-manager/hooks/use-file-manager';
import { useFileFilters } from 'features/file-manager/hooks/use-file-filters';
import { FileModals } from 'features/file-manager/components/modals/file-modals';
import { FileFilters } from 'features/file-manager/components/common-filters';
import { FileManagerHeaderToolbar } from 'features/file-manager/components/my-files/my-files-header-toolbar';
import { useFileManagerConfig } from 'features/file-manager/hooks/use-file-manager-config';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';

interface FileManagerMyFilesProps {
  onCreateFile?: () => void;
  defaultViewMode?: 'grid' | 'list';
  showTitle?: boolean;
}

export const FileManagerMyFiles: React.FC<FileManagerMyFilesProps> = ({ onCreateFile, defaultViewMode, showTitle = true }) => {
  const fileManager = useFileManager({ onCreateFile, defaultViewMode });
  const { filters, handleFiltersChange } = useFileFilters<FileFilters>({
    name: '',
    fileType: undefined,
    lastModified: undefined,
  });
  const config = useFileManagerConfig();

  const handleSearchChange = useCallback(
    (query: string) => {
      fileManager.handleSearchChange(query);
      handleFiltersChange({
        ...filters,
        name: query,
      });
    },
    [fileManager, handleFiltersChange, filters]
  );

  const buildFileActions = () => {
    const { actions } = config;
    return {
      ...(actions.viewDetails && { onViewDetails: fileManager.handleViewDetails }),
      ...(actions.download && { onDownload: fileManager.handleDownload }),
      ...(actions.share && { onShare: fileManager.handleShare }),
      ...(actions.delete && { onDelete: fileManager.handleDelete }),
      ...(actions.rename && { onRename: fileManager.handleRename }),
      ...(actions.sharedWithTenant && { onSharedWithTenant: fileManager.handleShareWithTenant }),
    };
  };

  const commonViewProps = {
    ...buildFileActions(),
    onRenameUpdate: fileManager.handleRenameUpdate,
    filters,
    newFiles: fileManager.newFiles,
    newFolders: fileManager.newFolders,
    renamedFiles: fileManager.renamedFiles,
    fileSharedUsers: fileManager.fileSharedUsers,
    filePermissions: fileManager.filePermissions
  };

  const headerToolbar = (
    <FileManagerHeaderToolbar
      viewMode={fileManager.viewMode}
      handleViewMode={fileManager.handleViewModeChange}
      searchQuery={fileManager.searchQuery}
      onSearchChange={handleSearchChange}
      filters={filters}
      onFiltersChange={(filters) =>
        handleFiltersChange({
          ...filters,
          name: filters.name ?? '',
        })
      }
      onFileUpload={(files) => fileManager.handleFileUpload(files, false)}
      onFolderCreate={(name) => fileManager.handleFolderCreate(name, false)}
      showTitle={showTitle}
    />
  );

  const modals = (
    <FileModals
      isRenameModalOpen={fileManager.isRenameModalOpen}
      onRenameModalClose={fileManager.handleRenameModalClose}
      onRenameConfirm={fileManager.handleRenameConfirm}
      fileToRename={
        fileManager.fileToRename
          ? { ...fileManager.fileToRename, isShared: !!fileManager.fileToRename.isShared }
          : null
      }
      isShareModalOpen={fileManager.isShareModalOpen}
      onShareModalClose={fileManager.handleShareModalClose}
      onShareConfirm={fileManager.handleShareConfirm}
      fileToShare={
        fileManager.fileToShare
          ? { ...fileManager.fileToShare, isShared: !!fileManager.fileToShare.isShared }
          : null
      }
      RenameModalComponent={RenameModal}
      ShareModalComponent={ShareWithMeModal}
      SharedWithTenantModalComponent={ConfirmationModal}
      isSharedWithTenantModalOpen={fileManager.isSharedWithTenantModalOpen}
      onSharedWithTenantModalClose={fileManager.handleSharedWithTenantModalClose}
      onSharedWithTenantConfirm={fileManager.handleSharedWithTenantConfirm}
    />
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <FileManagerLayout headerToolbar={headerToolbar} modals={modals}>
        <FileViewRenderer
          viewMode={fileManager.viewMode}
          GridComponent={MyFileGridView}
          ListComponent={MyFilesListView}
          commonViewProps={commonViewProps}
        />
      </FileManagerLayout>
    </div>
  );
};
